import { useCallback, useEffect, useRef, useState } from "react";
import { CashCoveragePeriodThunk } from "../../../../../redux/features/Settings/Statistics";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import FecthingPage from "../../CommonComponents/FecthingPage";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import { errorcode } from "../../../../../utils/configurationProvider";

/**
 * CashCoveragePeriod - A component that displays cash coverage analysis.
 * Shows day-wise debit amounts for the last 7 days, total current balance,
 * total debited amount, and predicted cash coverage period in weeks.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {string} props.flow - Transaction flow type: 'internal', 'bene', 'fieldForce', 'reimbursement', or 'homeDashboard'
 * @returns {JSX.Element} Rendered cash coverage period component
 */
const CashCoveragePeriod = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  // Dummy day-wise entries for fallback display
  const dummyDayWiseEntries = [
    { day: "01", amount: 12000 },
    { day: "02", amount: 25000 },
    { day: "03", amount: 18000 },
    { day: "04", amount: 22000 },
    { day: "05", amount: 30000 },
    { day: "06", amount: 27000 },
    { day: "07", amount: 15000 },
  ];
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    dayWiseEntries: dummyDayWiseEntries, // array of { day, amount }
    dayWiseTotalDebitAmount: 10000,
    totalDebitAmount: 9000000,
    predictedCashPeriod: 3,
    totalCurrentBalance: 9000000,
  };

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);
  const toastShownRef = useRef(false);

  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes("VStatistics"); // Global service for statistics.

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);

  // Select loading and error states from Redux
  const { CashCoveragePeriodThunkLoading, CashCoveragePeriodThunkError } =
    useSelector((state) => state.settingStatistics);

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /**
   * Processes API response and updates component state with cash coverage data.
   * On success: maps report data, shows success toast (once), disables fetch page.
   * On failure: resets to default data, conditionally enables fetch page, sets error.
   */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        // Update graph data with all report fields
        setGraphData({
          dayWiseEntries: reportData.day_wise_entries, // array of { day, amount }
          dayWiseTotalDebitAmount: reportData.day_wise_total_debit_amount,
          totalDebitAmount: reportData.total_debit_amount,
          predictedCashPeriod: reportData.predicted_cash_period,
          totalCurrentBalance: reportData.total_current_balance,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        // Enable fetch page unless it's a "not found" error (no data exists)
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;

        setIsError(true);
      }
    },
    [dispatch, entityId],
  );

  /**
   * Fetches cash coverage period data from API.
   * Uses entity ID from login data (no additional filters required).
   */
  const fetchData = async () => {
    const response = await dispatch(
      CashCoveragePeriodThunk({ entity_id: entityId }),
    );

    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /**
   * Effect: Auto-fetch data when fetch page is disabled (manual click or initial load).
   * Re-runs when fetchPageEnabled changes.
   */

  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled]);

  /**
   * Handler for manual "Fetch Data" button click.
   * Initiates fetch by enabling loading and disabling fetch page.
   */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === "homeDashboard" ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      {/* Header with title only (no filter options for this component) */}
      <GraphHeader
        handlingOptionsRequired={[]}
        titleText={"Cash coverage period"}
        flow={flow}
        autoFetch={autoFetch}
        hideTitle={flow === "homeDashboard" ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col relative Qsmd:border-t-primary-bg Qsmd:border-t-border">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={CashCoveragePeriodThunkLoading || fetchPageLoading}
          isError={isError}
          errorData={
            CashCoveragePeriodThunkError &&
            CashCoveragePeriodThunkError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          handleFunction={handleFetchClick}
        />

        {/* Main Content: Two-column layout on desktop (Qsmd+)*/}
        <div className="w-width-full gap-gap-md flex Qsmd:flex-row Qsmd:gap-gap-none flex-col-reverse">
          {/* Left Column (Desktop) / Bottom (Mobile): Day-wise Debit Amounts */}
          <div className="Qsmd:w-auto w-width-full h-auto flex flex-col bg-primary-nav justify-center items-center flex-shrink-0 overflow-y-auto Qsmd:rounded-b-none Qsmd:rounded-bl-border-radius-md rounded-b-border-radius-md">
            <div className="w-width-full px-padding-md pt-padding-md flex flex-col Qsmd:mt-margin-sm">
              <div className="text-text-secondary text-font-sm Qsmd:text-font-xxs font-semibold mb-margin-md">
                Amount debited day wise
              </div>
              <div className="flex flex-col gap-gap-sm w-width-full h-height-full Qsmd:pb-padding-md pb-padding-sm border-b-border border-b-primary-bg">
                {graphData?.dayWiseEntries?.map((val, index) => {
                  return (
                    <div className="flex justify-between">
                      <div className="text-text-secondary font-normal Qsmd:text-font-xxs text-font-xs">
                        {/* Format date as "DD MMM" (e.g., "01 Jan") */}
                        {dayjs(val?.date).format("DD MMM")}
                      </div>
                      <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                        {fetchPageEnabled
                          ? "-"
                          : "₹ " +
                            formatTotalBalance(val?.amount, "amount").amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Day-wise Total Row */}
            <div className="w-width-full h-auto flex justify-between mt-margin-sm mb-margin-md px-padding-md Qsmd:my-margin-base Qsmd:pt-padding-md Qsmd:pb-padding-lg ">
              <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs">
                Total
              </div>
              <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "₹ " +
                    formatTotalBalance(
                      graphData?.dayWiseTotalDebitAmount,
                      "amount",
                    ).amount}
              </div>
            </div>
          </div>

          {/* Right Column (Desktop) / Top (Mobile): Balance & Coverage Calculation */}
          <div className="w-width-full px-padding-md Qsmd:p-padding-md">
            <div className="flex flex-col gap-gap-sm Qsmd:gap-gap-lg Qmd:gap-gap-base justify-between h-height-full">
              {/* Upper Section: Current Balance / Debited Amount with Divide Icon */}
              <div className="flex flex-col gap-gap-sm Qsmd:gap-gap-lg h-height-full">
                {/* Total Current Balance Row */}
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-gap-xs">
                    <div className="text-text-primary font-sans text-font-xs">
                      Total current balance
                    </div>
                    <div className="text-text-secondary font-sans text-font-xxs">
                      across all owner accounts
                    </div>
                  </div>
                  <div className="text-settled font-sans text-font-sm font-semibold">
                    {fetchPageEnabled
                      ? "-"
                      : "INR " +
                        formatTotalBalance(
                          graphData.totalCurrentBalance,
                          "amount",
                        ).amount}
                  </div>
                </div>

                {/* Divide Icon - visual separator for formula */}
                <div className="flex justify-center">
                  <img
                    className="w-icon-sm h-icon-sm"
                    src={ICONS?.DIVIDE_WHITE}
                    alt="divide_white"
                  />
                </div>

                {/* Total Debited Amount Row */}
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-gap-xs">
                    <div className="text-text-primary font-sans text-font-xs">
                      Total debited amount
                    </div>
                    <div className="text-text-secondary font-sans text-font-xxs">
                      across all owner accounts last week
                    </div>
                  </div>
                  <div className="text-negative-act font-sans text-font-sm font-semibold">
                    {fetchPageEnabled
                      ? "-"
                      : "INR " +
                        formatTotalBalance(graphData.totalDebitAmount, "amount")
                          .amount}
                  </div>
                </div>
              </div>

              {/* Lower Section: Predicted Cash Coverage Period */}
              <div className="flex flex-col gap-gap-sm Qsmd:gap-gap-none">
                <div className="border-t border-primary-bg "></div>
                <div className="flex flex-col gap-gap-sm Qsmd:gap-gap-lg justify-between items-center relative">
                  {/* Equals icon (mobile only) */}
                  <img
                    className="Qsmd:hidden w-icon-sm h-icon-sm"
                    src={ICONS?.EQUAL_WITH_ROUNDNESS}
                    alt="equals_to_linear_white"
                  />
                  {/* Grid layout for formula: Label | Equals Icon (desktop) | Value */}
                  <div
                    className="grid w-width-full justify-between items-center"
                    style={{ gridTemplateColumns: "1fr 0.5fr 1fr" }}
                  >
                    <div className="flex flex-col gap-gap-xs">
                      <div className="text-text-primary font-sans text-font-xs text-nowrap">
                        Predicted cash coverage period
                      </div>

                      <div className="text-text-secondary font-sans text-font-xxs">
                        in weeks
                      </div>
                    </div>
                    {/* Equals icon (desktop only) - centered in middle column */}
                    <div className="flex justify-self-center items-center relative inset-x-0 mx-auto">
                      <img
                        className="hidden Qsmd:block w-icon-sm h-icon-sm"
                        src={ICONS?.EQUAL_WITH_ROUNDNESS}
                        alt="equals_to_linear_white"
                      />
                    </div>
                    <div className="text-text-secondary font-sans text-font-sm font-semibold text-right">
                      {fetchPageEnabled ? "-" : graphData?.predictedCashPeriod}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashCoveragePeriod;
